import { Box, Typography } from "@mui/material";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import UploadSVG from "../../../../assets/TailoredGeneration/icons/tg_upload_icon.svg";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import { TgDataset, TgDatasetImage } from "../../../../pages/NewTG/Datasets/store/tg-dataset-store.tsx";
import { TrainingMode } from "../../../../pages/NewTG/Models/store/tg-model-store.tsx";
import { canTgProjectGeneratePrefix, isLegacyTgRoute } from "../../../../pages/NewTG/utils/tg-utils.ts";
import BriaButton from "../../../common/BriaButton/BriaButton.tsx";
import BriaTooltip from "../../../common/BriaTooltip/BriaTooltip.tsx";
import FileUploader from "../../../common/FileUploader/FileUploader.tsx";
import LoadingPlaceholder from "../../../common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import styles from "./SelectTgUploadMethod.module.scss";

type SelectTgUploadMethodStepProps = {
	handleUploadImagesNext?: () => void;
	handleUseAdvancedNext?: () => void;
	fromDatasetPage?: boolean;
};

const SelectTgUploadMethod = ({
	handleUploadImagesNext,
	handleUseAdvancedNext,
	fromDatasetPage = false,
}: SelectTgUploadMethodStepProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.steps.uploadMethod" });
	const { projectId } = useParams();
	const navigate = useNavigate();
	const { tgStore } = useAppStore();
	const { tgProjectStore, tgModelStore, tgDatasetStore } = tgStore;
	const uploadingDatasetImages =
		tgDatasetStore.loadingUpdateDataset ||
		tgDatasetStore.loadingDuplicateDataset ||
		tgDatasetStore.loadingUploadImages ||
		tgDatasetStore.loadingRegenerate ||
		tgDatasetStore.loadingCreateDataset ||
		tgDatasetStore.loadingGeneratePrefix;

	const createAndUploadDataset = async (e: ChangeEvent<HTMLInputElement>) => {
		const files: File[] | null = Array.from(e.target.files || []);
		e.target.value = "";

		if (!files.length) return;

		try {
			let newDataset: TgDataset;

			newDataset = await tgDatasetStore.createDataset({
				...tgDatasetStore.datasetForm,
				project_id: Number(projectId),
			});

			await tgDatasetStore.uploadImages(newDataset.id, files);
			const supportedImages: TgDatasetImage[] = tgDatasetStore.datasetForm.images.filter((image) => !image.error);

			if (canTgProjectGeneratePrefix(tgProjectStore.projectToView.ip_type)) {
				const supportedImagesUrls: string[] = supportedImages.map((image) => image.image_url);
				const generatedPrefix = await tgDatasetStore.imagesSampleGeneratePrefix(tgProjectStore.projectToView, {
					imagesURLs: supportedImagesUrls,
				});

				if (generatedPrefix) {
					newDataset.caption_prefix = generatedPrefix.prefix;
				}
			}

			newDataset.images = supportedImages;
			await tgDatasetStore.updateDataset(newDataset.id, newDataset);

			tgDatasetStore.datasetForm = { ...newDataset, images: tgDatasetStore.datasetForm.images };
			if (!fromDatasetPage) {
				tgModelStore.modelForm.dataset_id = newDataset.id;
			}

			if (fromDatasetPage) {
				const editDatasetState = {
					datasetToEdit: toJS(tgDatasetStore.datasetForm),
				};
				{
					const base = isLegacyTgRoute()
						? RouterConstants.NEW_TG.LEGACY.PROJECTS.fullPath
						: RouterConstants.NEW_TG.PROJECTS.fullPath;
					navigate(`${base}/${projectId}/${RouterConstants.NEW_TG.DATASETS.path}/${newDataset.id}/edit`, {
						state: editDatasetState,
					});
				}
			}

			if (handleUploadImagesNext) {
				handleUploadImagesNext();
			}
		} catch (error) {
			console.error("Error creating or uploading dataset:", error);
			tgDatasetStore.loadingCreateDataset = false;
			tgDatasetStore.loadingGeneratePrefix = false;
			tgDatasetStore.loadingUploadImages = false;
		}
	};

	return (
		<LoadingPlaceholder isLoading={tgDatasetStore.loadingDataset} className={styles.stepWrapper}>
			<Box className={styles.stepWrapper}>
				<Box className={styles.content}>
					<Typography className={styles.header}>{t("header")}</Typography>
					<Typography className={styles.description}>{t("light.description")}</Typography>
					<FileUploader
						buttonText={t("uploadButton")}
						buttonType="primaryMedium"
						onUpload={createAndUploadDataset}
						maxFilesLimit={200}
						inputProps={{ accept: "image/png, image/jpg, image/jpeg, image/webp", multiple: true }}
						buttonProps={{ startIcon: <img src={UploadSVG} className={styles.uploadIcon} /> }}
						loading={uploadingDatasetImages}
						disabled={uploadingDatasetImages}
					/>
					<Typography className={styles.divider}></Typography>
					<Typography className={styles.advancedText}>
						Have more than 200 high-quality images <strong>OR</strong> already have the captions ready?
					</Typography>
					<BriaTooltip
						title={t("advancedDatasetTooltip")}
						disable={
							tgModelStore.modelForm.training_mode !== TrainingMode.FULLY_AUTOMATED || fromDatasetPage
						}
					>
						<BriaButton
							buttonType="textMedium"
							disabled={
								tgModelStore.modelForm.training_mode === TrainingMode.FULLY_AUTOMATED &&
								!fromDatasetPage
							}
							className={styles.advancedLink}
							onClick={handleUseAdvancedNext}
						>
							<img src={UploadSVG} className={styles.advancedIcon} />
							{t("advancedButton")}
						</BriaButton>
					</BriaTooltip>
					<Typography className={styles.supportedFiles}>{t("supportedFiles")}</Typography>
				</Box>
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(SelectTgUploadMethod);
